import { useInView } from '@/hooks/useInView';

// Featured testimonial: named executive, real photo, company logo.
// Wording approved by Nick Daniels, 2026-07-05.
const featured = {
  stat: 'Real numbers the same afternoon',
  quote:
    'We connected CodePulse to our GitHub org and had real numbers the same afternoon. For the first time I can see where delivery actually slows down across our teams, not just that it slowed down. It has changed the conversations we have in leadership meetings.',
  name: 'Nick Daniels',
  title: 'CPO & Founder, Alliants',
  photo: '/images/customers/nick-daniels.jpg',
  logo: '/images/customers/alliants-logo.png',
  logoAlt: 'Alliants',
};

const testimonials = [
  {
    stat: '31% of PRs idle - fixed in 3 weeks',
    quote:
      "I'd been telling the CTO we were slow because of tech debt. Turns out almost a third of our PRs were just sitting there waiting on the same two reviewers. We redistributed reviews and the difference was noticeable within a couple weeks.",
    name: 'Robert Striker',
    title: 'VP of Engineering at Article Fiesta',
    initials: 'RS',
  },
  {
    stat: 'Review time: ~3 days → same-day',
    quote:
      "Our retros used to be a blame game - everyone had a theory, nobody had receipts. Now I just pull up the dashboard and it's right there. We found out review turnaround was almost three days on average. Got it down to same-day once we actually saw it.",
    name: 'Jonathon Turner',
    title: 'Engineering Manager at Ultra Communications',
    initials: 'JT',
  },
];

export default function TestimonialQuote() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.2 });

  return (
    <section ref={sectionRef} className="py-10 bg-white dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-8 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            What Engineering Leaders Discovered
          </h2>
        </div>

        {/* Featured testimonial - named exec, photo, company logo */}
        <figure
          className={`mb-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/20 dark:to-gray-800 p-8 sm:p-10 ${
            isInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="flex-shrink-0 flex flex-col items-center text-center md:w-48">
              <img
                src={featured.photo}
                alt={`${featured.name}, ${featured.title}`}
                width={112}
                height={112}
                loading="lazy"
                className="w-28 h-28 rounded-full object-cover ring-4 ring-white dark:ring-gray-700 shadow-md"
              />
              <img
                src={featured.logo}
                alt={featured.logoAlt}
                width={120}
                height={30}
                loading="lazy"
                className="mt-5 h-7 w-auto object-contain"
              />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-bold text-primary-600 dark:text-primary-400 mb-3">
                {featured.stat}
              </div>
              <blockquote className="text-xl text-gray-800 dark:text-gray-200 leading-relaxed">
                &ldquo;{featured.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-5">
                <p className="font-semibold text-gray-900 dark:text-white">{featured.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{featured.title}</p>
              </figcaption>
            </div>
          </div>
        </figure>

        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((t) => (
            <div
              key={t.initials}
              className={`bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 ${
                isInView ? 'animate-fade-in-up' : 'opacity-0'
              }`}
            >
              <div className="text-lg font-bold text-primary-600 dark:text-primary-400 mb-3">
                {t.stat}
              </div>
              <blockquote className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                    {t.initials}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{t.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
